import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Outlet } from "react-router-dom";
import { StyleObject } from "styletron-react";
import { colors, frames, glyphs, mediaQueries, mediaQueriesNumbers, useMediaQuery } from "../../Styles";
import { Overlay, CoreNavLink } from "../../Components";
import { NavLinkTypes } from "../../Definitions";
import { Pages } from "../root";
import { random } from "lodash";
import NewButton from "../../Components/BaseComponents/Buttons/newButton";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../Store";

interface TabMenu_Props {
  main: Pages;
  contexts: Pages[];
  toolsMenu?: boolean;
  overrides?: {
    gridArea?: string;
    base_alternative?: Pages;
  };
}

export default function TabMenu({ overrides, main, toolsMenu, contexts }: TabMenu_Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const [showTabs, setShowTabs] = useState<boolean>(false);
  const maxWidth = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  const maxHeight = useMediaQuery(`(max-height: ${mediaQueriesNumbers.horizontalMobileMax}px)`);
  const mobileXs = maxWidth || maxHeight
  const [activeTab, setActiveTab] = useState<string>(main);
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);

  // _Root Overrides (Grid Area)
  const _Root = useMemo<StyleObject>(() => {
    return Object.assign(
      {} as StyleObject,
      overrides && overrides.gridArea ? { gridArea: overrides.gridArea } : {}
    );
  }, [overrides]);

  // Toggle tabs
  const toggleComponents = () => {
    setShowTabs(!showTabs);
  };

  useEffect(() => {
    setShowTabs(false);
  }, [location]);

  // #region | Tab context
  const getNavLinkPath = (contextPage: Pages | undefined, isMainTab: boolean): string => {
    if (isMainTab) {
      return "."; 
    }
    return `${contextPage}`;
  };

  const menuItems = useMemo(() => {
    const items = [];
    items.push({
      text: t(`menuHeaders.${main}`, { defaultValue: main }),
      path: getNavLinkPath(undefined, true),
      isMainTab: true,
    });
    contexts.forEach((context) => {
      items.push({
        text: t(`menuHeaders.${context}`, { defaultValue: context }),
        path: getNavLinkPath(context, false),
        isMainTab: false,
      });
    });
    return items;
  }, [main, contexts, t]);

  // Find activeTab from path
  useEffect(() => {
    const currentPath = decodeURIComponent(location.pathname);
  
    const parts = currentPath.split("/");
    const lastSegment = parts[parts.length - 1];
  
    const matchedContext = contexts.find((ctx) => ctx === lastSegment);
  
    if (matchedContext) {
      setActiveTab(matchedContext);
    } else {
      setActiveTab(main);
    }
  }, [location, contexts, main]);
  // #endregion

  return (
    <frames.Page_outer>
      <frames._page_outer_tabMenu>
        <frames.TabMenu $style={_Root}>
          {mobileXs ? ( undefined ) : (
            <div style={{ display: "flex", gap: "5px" }}>
              {menuItems.map((item, index) => (
                <CoreNavLink
                  key={"tab"+index+random(0, 9999)}
                  content={item.text}
                  path={item.path}
                  endPath={item.isMainTab}
                  navLinkType={NavLinkTypes.tabNavLink}
                  overrides={{
                    baseButton: { borderRadius: menuItems[index].isMainTab ? "0px 10px 0px 0px" : "10px 10px 0px 0px" }
                  }}
                />
              ))}
            </div>
          )}
        </frames.TabMenu>

        {/* TabMenu bottom line */}
        {mobileXs ? ( undefined ) : ( <div style={{ height: "5px", backgroundColor: colors.blueDarkPrimary, position: "sticky", left: "0px", right: "0px" }}/> )}
      </frames._page_outer_tabMenu>

      {/* TabMenu Btn on Mobile */}
      {mobileXs ? createPortal (
        <div style={{ position: "absolute", right: "0px", bottom: "10px", zIndex: editMode ? "9" : "999"}}>
          <NewButton
            content={t(`menuHeaders.${activeTab}`, { defaultValue: activeTab})}
            onClick={toggleComponents}
            overrides={{
              baseStyles: { width: "fit-content", minWidth: "60px", height: "60px", backgroundColor: colors.blueDarkPrimary + "cc", borderStyle: "solid", borderColor: colors.blueDarkPrimary, borderWidth: "0px 2px 2px 2px", borderRadius: "10px 0px 0px 10px", zIndex: "999", backdropFilter: "blur(2px)" },
              hoverStyles: { backgroundColor: colors.blueDarkPrimary + "cc", minWidth: "60px", height: "60px" },
            }}
          />
        </div>, document.body
      ) : ( undefined )}
      {/* TabMenu on Mobile */}
      {showTabs && createPortal(
        <Overlay
          overrides={{ zIndex: 99999, blur: true, genericStyle: { display: "flex", flexDirection: "column", justifyContent: "end", alignItems: "flex-end", padding: "20px", boxSizing: "border-box" } }}
          content={ <>
            <div onClick={toggleComponents} style={{ cursor: "pointer" }}>
              <glyphs.Close size="25px" />
            </div>
            <div style={{
              backgroundColor: colors.greyDarkPrimary,
              borderWidth: "10px 1px",
              borderStyle: "solid",
              borderColor: colors.greyDarkPrimary,
              borderRadius: "10px",
              width: "100%",
              height: "fit-content",
              boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d",
              maxHeight: "100%",
              overflowY: "auto",
              padding: "0px 9px",
              boxSizing: "border-box",
              scrollbarWidth: "thin",
              scrollbarColor: colors.greyDarkSecondary + colors.transparent,
            }}>
              <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "10px" }}>
                {menuItems.map((item, index) => (
                  <CoreNavLink
                    content={item.text}
                    path={item.path}
                    endPath={item.isMainTab}
                    navLinkType={NavLinkTypes.systemNavLink}
                  />
                ))}
              </div>
            </div>
          </> }
        />, document.body
      )}
      <frames._page_outer_inner_page $toolsMenu={toolsMenu}>
        <Outlet />
      </frames._page_outer_inner_page>
    </frames.Page_outer>
  );
}

